/**
 * sprayWallTab — the Spraywall tab's own routing and visibility rules, pure.
 *
 * Two layers of routing share the URL hash (plan §9.1). The main handler in
 * `useMainApp` reads only the FIRST segment (`spraywall`) to pick the tab; the
 * tab then parses the rest itself — on mount and on every `hashchange` — so a
 * deep link that arrives while the tab is already open (a push, a map marker)
 * still lands on the right wall. Everything below the first segment lives
 * here, so the parser and the hash builder cannot drift apart.
 *
 * The bounce rule is split in two on purpose (§9.1): "the add-on is off" and
 * "you are not an active member" are known early and bounce at once, while
 * "is there a published wall?" can only be answered after the first
 * `sprayWalls` snapshot — bouncing on the empty pre-snapshot list would throw
 * every deep link back to the map. Hence the third outcome, `"wait"`.
 *
 * The problem routes (21g "Routes and hashes", planner DEVIATION 2): the
 * third segment `new` is the problem editor in create mode (`problemNew`;
 * a problem id is a 20-character auto-id and cannot collide), and a fourth
 * segment `edit` after a problem id is the editor in edit mode
 * (`problemEdit`). Any other fourth segment is ignored — the problem view.
 *
 * The tab's last-location memory (per gym, per browser session) lives here
 * too, next to the parser it builds on.
 *
 * `#spraywall/log` still PARSES (to `view: "log"`) so the tab can turn an old
 * bookmark into the redirect to the Statistics tab; it is never remembered and
 * never restored — the member's spray log is not a page of this tab any more.
 *
 * docs/spec/21j-spraywall-tab-navigation.md, "The tab" and "Navigation and hash routing";
 * docs/spec/21g-spraywall-problems-ui.md, "Routes and hashes".
 */
import { SPRAY_WALL_TAB, sprayWallHash } from "./sprayWallMap";
import { sessionStore } from "./safeStorage";

// Second-segment words that are never a wall id (`new` only as `new/edit`).
const LOG_SEGMENT = "log";
const NEW_SEGMENT = "new";
// Third-segment words that name a view rather than a problem id.
const VIEW_SEGMENTS = new Set(["lists", "manage", "edit"]);
// Third segment: the problem editor in create mode. Fourth segment after a
// problem id: the problem editor in edit mode.
const PROBLEM_NEW_SEGMENT = "new";
const PROBLEM_EDIT_SEGMENT = "edit";
// A reserved word in the wall-id position is not a wall.
const RESERVED_WALL_SEGMENTS = new Set([...VIEW_SEGMENTS, LOG_SEGMENT, NEW_SEGMENT]);

const PICKER = Object.freeze({ wallId: null, view: "picker", create: false, problemId: null });

function decodeSegment(s) {
  try { return decodeURIComponent(s); } catch { return s; }
}

/**
 * `#spraywall…` → `{ wallId, view, create, problemId }`.
 * Anything that is not a Spraywall hash, or does not name a wall, is the picker.
 */
export function parseSprayWallHash(hash) {
  const raw = typeof hash === "string" ? hash.replace(/^#/, "") : "";
  const segs = raw.split("/").map(decodeSegment);
  if (segs[0] !== SPRAY_WALL_TAB) return { ...PICKER };
  const first = segs[1] || "";
  if (!first) return { ...PICKER };
  if (first === LOG_SEGMENT) return { wallId: null, view: "log", create: false, problemId: null };
  if (first === NEW_SEGMENT) {
    return segs[2] === "edit"
      ? { wallId: null, view: "edit", create: true, problemId: null }
      : { ...PICKER };
  }
  if (RESERVED_WALL_SEGMENTS.has(first)) return { ...PICKER };
  const second = segs[2] || "";
  if (!second) return { wallId: first, view: "problems", create: false, problemId: null };
  if (VIEW_SEGMENTS.has(second)) return { wallId: first, view: second, create: false, problemId: null };
  if (second === PROBLEM_NEW_SEGMENT) return { wallId: first, view: "problemNew", create: false, problemId: null };
  if (segs[3] === PROBLEM_EDIT_SEGMENT) return { wallId: first, view: "problemEdit", create: false, problemId: second };
  return { wallId: first, view: "problems", create: false, problemId: second };
}

/** The inverse of {@link parseSprayWallHash}. */
export function sprayWallTabHash({ wallId = null, view = "picker", create = false, problemId = null } = {}) {
  if (view === "log") return sprayWallHash(LOG_SEGMENT);
  if (view === "edit" && create) return sprayWallHash(NEW_SEGMENT, "edit");
  if (!wallId || view === "picker") return sprayWallHash();
  if (view === "lists" || view === "manage" || view === "edit") return sprayWallHash(wallId, view);
  if (view === "problemNew") return sprayWallHash(wallId, PROBLEM_NEW_SEGMENT);
  // An edit route without a problem has nothing to edit: the wall view.
  if (view === "problemEdit" && problemId) return `${sprayWallHash(wallId, problemId)}/${PROBLEM_EDIT_SEGMENT}`;
  return sprayWallHash(wallId, problemId || undefined);
}

/**
 * Navigate inside the tab. `pushState` (or `replaceState`) is silent, so a
 * synthetic `hashchange` follows — the tab re-parses on it, like after
 * `setTab`. Writing the hash already shown is a no-op: it would only add a
 * duplicate history entry.
 *
 * A pushed entry remembers the hash it was pushed FROM (`{ sprayFrom }` as
 * the history state; nothing else in the app reads `history.state`), and a
 * replace keeps that memory. {@link backToSprayWallHash} uses it to step back
 * instead of stacking a duplicate entry.
 */
export function pushSprayWallHash(hash, { replace = false } = {}) {
  if (typeof window === "undefined") return;
  if (window.location.hash === hash) return;
  const url = window.location.pathname + window.location.search + hash;
  if (replace) window.history.replaceState(window.history.state ?? null, "", url);
  else window.history.pushState({ sprayFrom: window.location.hash }, "", url);
  try { window.dispatchEvent(new HashChangeEvent("hashchange")); } catch {}
}

/**
 * Leave the current tab route for `hash` the way Back would: when the current
 * history entry was pushed from exactly `hash` (the problem view opened from
 * the list, the editor opened from the view), step back — the history then
 * holds no duplicate of the page just left, and Back does not return to a
 * closed editor or a deleted problem. Otherwise (a deep link, a shared URL)
 * replace the current entry with `hash`.
 */
export function backToSprayWallHash(hash) {
  if (typeof window === "undefined") return;
  const state = window.history.state;
  if (state && typeof state === "object" && state.sprayFrom === hash && window.location.hash !== hash) {
    window.history.back();
    return;
  }
  pushSprayWallHash(hash, { replace: true });
}

// ── The last location: where the member was in the tab, per gym, per session ──
// docs/spec/21j-spraywall-tab-navigation.md, "Returning to the tab — the last location".

const LAST_LOCATION_PREFIX = "sendly.spraywall.lastLocation.";
// Without sessionStorage (a private window, blocked site data) the memory
// lives for the page's lifetime here.
const lastLocationFallback = new Map();
// The views worth returning to; the editors, the management area and the
// `log` redirect are left out on purpose (the memory keeps the page they were
// opened from).
const REMEMBERED_VIEWS = new Set(["picker", "problems", "lists"]);

/** The `sessionStorage` key of a gym's remembered location. */
export function sprayLastLocationKey(gymId) {
  return `${LAST_LOCATION_PREFIX}${gymId}`;
}

/** The hash to remember for a parsed route, or `null` (leave the memory as it is). */
export function rememberedSprayHash(route) {
  if (!route || !REMEMBERED_VIEWS.has(route.view)) return null;
  if (route.view === "problems" || route.view === "lists") {
    if (!route.wallId) return null;
    return sprayWallTabHash({ wallId: route.wallId, view: route.view, problemId: route.view === "problems" ? route.problemId : null });
  }
  return sprayWallTabHash({ view: route.view });
}

/** The remembered hash of a gym, or `null`. */
export function readSprayLastLocation(gymId) {
  if (!gymId) return null;
  const key = sprayLastLocationKey(gymId);
  // A write that storage refused (full, blocked) is newer than whatever
  // storage still holds, so the in-memory copy answers first.
  if (lastLocationFallback.has(key)) return lastLocationFallback.get(key);
  // Storage unreadable and nothing refused in memory: no location.
  const raw = sessionStore.get(key);
  return typeof raw === "string" && raw ? raw : null;
}

/** Remember `hash` as the gym's last location: in the session, or in memory when storage refuses. */
export function writeSprayLastLocation(gymId, hash) {
  if (!gymId || typeof hash !== "string" || !hash) return;
  const key = sprayLastLocationKey(gymId);
  if (sessionStore.set(key, hash)) {
    lastLocationFallback.delete(key);
    return;
  }
  // Storage unavailable or full: the in-memory copy answers.
  lastLocationFallback.set(key, hash);
}

/**
 * The hash to restore on entering the tab, or `null` (stay on `#spraywall`).
 * A wall page, its lists or an open problem resolve only while the wall is
 * among the viewer's visible `walls` (a problem that is gone is the pane's to
 * fall back from, once its document answers). Anything else — the picker, a
 * `log` memory written before the Napló moved to the Statistics tab, a vanished
 * or hidden wall — is `null`, i.e. the bare tab.
 */
export function sprayRestoreHash(remembered, walls) {
  if (typeof remembered !== "string" || !remembered) return null;
  if (remembered.replace(/^#/, "").split("/")[0] !== SPRAY_WALL_TAB) return null;
  const route = parseSprayWallHash(remembered);
  if (route.view !== "problems" && route.view !== "lists") return null;
  if (!route.wallId || !(walls || []).some((w) => w && w.id === route.wallId)) return null;
  return rememberedSprayHash(route);
}

/** A wall a member may see: shown, published, not being deleted. */
export function isWallPublishedForMembers(wall) {
  return !!wall && wall.active === true && !!wall.currentVersionId && !wall.deleting;
}

/**
 * Whether the tab is in the bar. A manager sees it before the first wall
 * exists — someone has to be able to create it.
 */
export function sprayWallTabVisible({ addonOn, memberState, walls, canManage } = {}) {
  if (!addonOn || memberState !== "active") return false;
  if (canManage) return true;
  return (walls || []).some(isWallPublishedForMembers);
}

/**
 * What the shell does while the tab is selected: `"show"`, `"wait"` (keep the
 * tab with a loader) or `"bounce"` (back to the map).
 *
 * An unloaded gym doc is a wait, not a bounce: the add-on state is not known
 * yet, and bouncing then would rewrite every `#spraywall…` deep link on a cold
 * start.
 */
export function sprayWallTabDecision({ gymLoaded, addonOn, memberState, wallsLoaded, walls, canManage } = {}) {
  if (gymLoaded && !addonOn) return "bounce";
  if (memberState === "none") return "bounce";
  if (!gymLoaded) return "wait";
  if (!wallsLoaded || memberState === "loading") return "wait";
  return sprayWallTabVisible({ addonOn, memberState, walls, canManage }) ? "show" : "bounce";
}

/** The walls a viewer gets in the tab, in `order` (name as the tie-break). */
export function wallsForViewer(walls, canManage) {
  const list = (walls || []).filter((w) => w && (canManage || isWallPublishedForMembers(w)));
  return list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)
    || (a.name || "").localeCompare(b.name || ""));
}
